// eslint-disable-next-line import/no-extraneous-dependencies
import { RiSearchLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactNode,
    useEffect, useState, useCallback,
} from "react";
import Dropdown from "../theme/dropdown/Dropdown";
import FormGroup from "../theme/form-group/FormGroup";
import {
    FilterPropCategory, FilterPropGroupItem, isDivider, isGroup,
} from "./Filters";

interface FilterProps<TIsMulti = true, TValue = never> {
    categories?: FilterPropCategory<TValue>[];
    selectedCategories?: TIsMulti extends true ? Set<string> : string;
    onSelected?: (ids: (TIsMulti extends true ? Set<string> : string)) => void;
    onSetSearch?: (value: string) => void;
    onSearch?: (value: string) => Promise<FilterPropCategory<TValue>[]>;
    isMulti?: TIsMulti;
    canSelect?: boolean;
    searchMinimum?: number;
    searchMaximum?: number;
    shouldReorderSelected?: boolean;
    className?: string;
    itemClassName?: string;
    searchPlaceholder?: string;
    metricPathPrefix?: string[]
}

const MAX_SEARCH = 10;

const FilterPanel = React.forwardRef(<TIsMulti extends boolean = false, TValue = never>({
    categories: categoriesList, selectedCategories, onSelected, onSetSearch,
    isMulti, className, searchMaximum,
    canSelect, searchMinimum, onSearch, shouldReorderSelected, itemClassName,
    searchPlaceholder, metricPathPrefix,
}: FilterProps<TIsMulti, TValue>, ref: React.ForwardedRef<HTMLDivElement>): ReactNode => {
    const [listSearch, setListSearch] = useState("");
    const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();

    const [categories, setCategories] = useState<FilterPropCategory<TValue>[]>(categoriesList ?? []);

    const categoriesFlat = categories?.map(row => (isGroup(row) ? row.options : row)).flat();
    let results = categories ?? [];

    useEffect(() => {
        if (categoriesList) {
            setCategories(categoriesList);
        }
    }, [categoriesList]);

    useEffect(() => {
        if (!onSearch) {
            return;
        }

        clearTimeout(searchTimer);
        setSearchTimer(setTimeout(async () => {
            setCategories(await onSearch(listSearch));
        }, 500));
    }, [listSearch]);

    const onClick = useCallback((value: string) => {
        if (!isMulti) {
            (onSelected as ((val: string) => void))?.(value);
            return;
        }

        const selected = selectedCategories as Set<string> ?? new Set();
        if (selected.has(value)) {
            selected.delete(value);
        } else {
            selected.add(value);
        }

        let selectedValue = Array.from(selected);
        if (!listSearch) {
            const flat = categoriesFlat?.mapFilter(row => (!isDivider(row) ? row.value : undefined)) ?? [];
            selectedValue = selectedValue.union(flat);
        }
        const callback = onSelected as ((val: Set<string>) => void);
        callback?.(new Set(selectedValue));
    }, [isMulti, selectedCategories, categories]);

    if (categories?.isEmpty && !onSetSearch) {
        return <div className={className} />;
    }

    if (listSearch) {
        results = results?.mapFilter(row => {
            if (isDivider(row)) {
                return row;
            }
            if (isGroup(row)) {
                return {
                    label: row.label,
                    options: row.options.filter(child => (child.text ?? child.label as string)
                        .toLowerCase?.()
                        .includes(listSearch.toLowerCase())),
                };
            }
            if ((row.text ?? row.label as string).toLowerCase?.().includes(listSearch.toLowerCase())) {
                return row;
            }
            return undefined;
        });
    }
    const searchAmount = searchMaximum || MAX_SEARCH;
    const canSearch = onSearch || (searchMinimum && categoriesFlat && categoriesFlat?.length > searchMinimum);
    if (canSearch) {
        results = results?.limit(searchAmount);
    }

    const renderItem = (item: FilterPropGroupItem<TValue>) => {
        const isSelected = canSelect && (
            ((selectedCategories instanceof Set)
        && selectedCategories.has(item.value)) || selectedCategories === item.value
        );
        return (
            <Dropdown.Item
                key={item.value}
                eventKey={item.value}
                isActive={isSelected}
                isChecked={canSelect && isSelected}
                className={itemClassName}
                onClick={() => onClick(item.value)}
                metricPath={metricPathPrefix
                    ? [...metricPathPrefix, item.value]
                    : undefined}
            >
                {item.label}
            </Dropdown.Item>
        );
    };

    const selectedSet = canSelect && (selectedCategories instanceof Set) ? selectedCategories : new Set(selectedCategories);

    return categories?.isNotEmpty ? (
        <div
            className={classNames("p3-1 w-100 menu-panel", className)}
            ref={ref}
        >
            {canSearch && (
                <FormGroup
                    size="filter"
                    isRequired
                    type="search"
                    prefixIcon={<RiSearchLine />}
                    placeholder={searchPlaceholder}
                    aria-label="Search"
                    value={listSearch}
                    groupClassName="m3b-1"
                    autoFocus
                    onChange={event => setListSearch(event.target.value)}
                />
            )}

            {listSearch && results.isEmpty && (
                <div className={
                    classNames("mx-auto fst-italic w-100 text-center mt-2", "text-dim")
                }
                >
                    No results found.
                </div>
            )}

            {shouldReorderSelected && canSearch && results?.mapFilter(item => {
                if (isDivider(item)) {
                    return <Dropdown.Divider />;
                } if (isGroup(item)) {
                    const children = item.options.filter(child => selectedSet.has(child.value));
                    return children.isNotEmpty ? (
                        <div key={item.label as string}>
                            <div>{item.label}</div>
                            {children.limit(searchMinimum ?? -1).map(renderItem)}
                        </div>
                    ) : undefined;
                }
                return selectedSet.has(item.value) ? renderItem(item) : undefined;
            })}

            {results?.mapFilter((item, index) => {
                if (isDivider(item)) {
                    return <Dropdown.Divider />;
                } if (isGroup(item)) {
                    let children = item.options;
                    if (canSearch && shouldReorderSelected) {
                        children = children.filter(child => !selectedSet.has(child.value));
                    }
                    const isLast = index === results.length - 1;
                    return children.isNotEmpty ? (
                        <div key={item.label as string}>
                            <Dropdown.Header>{item.label}</Dropdown.Header>
                            {children.limit(searchMinimum ?? -1).map(renderItem)}
                            {!isLast && (
                                <Dropdown.Divider />
                            )}
                        </div>
                    ) : undefined;
                }
                return (
                    !canSearch || !shouldReorderSelected || !selectedSet.has(item.value))
                    ? renderItem(item)
                    : undefined;
            })}

        </div>
    ) : undefined;
});

FilterPanel.defaultProps = {
    categories: [],
    selectedCategories: undefined,
    onSelected: undefined,
    isMulti: true,
    onSetSearch: undefined,
    className: "",
    canSelect: true,
    searchMinimum: 10,
    searchMaximum: MAX_SEARCH,
    onSearch: undefined,
    shouldReorderSelected: true,
    itemClassName: "",
    searchPlaceholder: "Search...",
    metricPathPrefix: undefined,
};

export default FilterPanel as <
    TMulti extends boolean = false,
    TValue = never
> (
    props: FilterProps<TMulti, TValue> & { ref?: React.ForwardedRef<HTMLElement> }
) => ReturnType<typeof FilterPanel>;
